import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { advanceAPI } from '../../../services';
import { 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle,
  TrendingUp,
  Users,
  AlertCircle,
  Send,
  Ban,
  Filter,
  RefreshCw,
  BarChart3
} from 'lucide-react';

const AdvanceManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDisburseModal, setShowDisburseModal] = useState(false);
  const [emiMonths, setEmiMonths] = useState('');
  const [comments, setComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [disbursementMode, setDisbursementMode] = useState('bank_transfer');
  const [disbursementReference, setDisbursementReference] = useState('');

  // Check permission
  const hasPermission = user?.permissions?.canManageAdvances || 
                       ['admin', 'superadmin'].includes(user?.role);

  useEffect(() => {
    if (hasPermission) {
      fetchData();
    }
  }, [activeTab, hasPermission]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch requests based on active tab
      let response;
      if (activeTab === 'pending') {
        response = await advanceAPI.getPendingAdvanceRequests();
      } else {
        response = await advanceAPI.getAllAdvanceRequests({ status: activeTab === 'all' ? undefined : activeTab });
      }
      setRequests(response?.data || []);

      // Fetch statistics
      const statsResponse = await advanceAPI.getAdvanceStatistics();
      setStatistics(statsResponse?.data || null);
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMessage('Failed to load data');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (selectedRequest.type === 'loan' && (!emiMonths || emiMonths <= 0)) {
      setErrorMessage('Please enter valid EMI months for loan');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      await advanceAPI.approveAdvanceRequest(selectedRequest._id, {
        emiMonths: selectedRequest.type === 'loan' ? parseInt(emiMonths) : undefined,
        comments
      });
      
      setSuccessMessage('Request approved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowApproveModal(false);
      setSelectedRequest(null);
      setEmiMonths('');
      setComments('');
      fetchData();
    } catch (error) {
      console.error('Error approving request:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to approve request');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setErrorMessage('Please provide a rejection reason');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      await advanceAPI.rejectAdvanceRequest(selectedRequest._id, {
        reason: rejectionReason
      });
      
      setSuccessMessage('Request rejected');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to reject request');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleDisburse = async () => {
    try {
      await advanceAPI.disburseAdvance(selectedRequest._id, {
        disbursementMode,
        disbursementReference
      });
      
      setSuccessMessage('Marked as disbursed successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowDisburseModal(false);
      setSelectedRequest(null);
      setDisbursementMode('bank_transfer');
      setDisbursementReference('');
      fetchData();
    } catch (error) {
      console.error('Error disbursing:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to mark as disbursed');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!hasPermission) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Access Denied
            </h3>
            <p className="text-red-800">
              You do not have permission to manage advances/loans. Please contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <DollarSign className="w-7 h-7 text-green-600" />
          Advance/Loan Management
        </h1>
        <p className="text-gray-600 mt-1">
          Review and manage employee advance and loan requests
        </p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {errorMessage && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <span className="text-2xl font-bold text-yellow-900">{statistics.pending}</span>
            </div>
            <p className="text-sm text-yellow-800 font-medium">Pending</p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-2xl font-bold text-green-900">{statistics.approved}</span>
            </div>
            <p className="text-sm text-green-800 font-medium">Approved</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="text-2xl font-bold text-blue-900">{statistics.disbursed}</span>
            </div>
            <p className="text-sm text-blue-800 font-medium">Disbursed</p>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              <span className="text-lg font-bold text-purple-900">
                {formatCurrency(statistics.totalAmount)}
              </span>
            </div>
            <p className="text-sm text-purple-800 font-medium">Total Amount</p>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <span className="text-lg font-bold text-orange-900">
                {formatCurrency(statistics.totalOutstanding)}
              </span>
            </div>
            <p className="text-sm text-orange-800 font-medium">Outstanding</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 flex items-center gap-2 border-b border-gray-200">
        {['pending', 'approved', 'disbursed', 'all'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab}
          </button>
        ))}
        
        <button
          onClick={fetchData}
          className="ml-auto flex items-center gap-2 px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Requests Found</h3>
          <p className="text-gray-600">
            No {activeTab} requests at the moment.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request._id}
              className="bg-white rounded-lg shadow-md border border-gray-200 p-6"
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Employee Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Employee Details</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-600">Name:</span> <span className="font-medium">{request.employee?.name}</span></p>
                    <p><span className="text-gray-600">ID:</span> {request.employee?.employeeId}</p>
                    <p><span className="text-gray-600">Department:</span> {request.employee?.department || 'N/A'}</p>
                    <p><span className="text-gray-600">Designation:</span> {request.employee?.designation || 'N/A'}</p>
                    {request.employee?.salary?.annualCTC && (
                      <p><span className="text-gray-600">Salary:</span> {formatCurrency(request.employee.salary.annualCTC)}/year</p>
                    )}
                  </div>
                </div>

                {/* Request Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Request Details</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-600">Type:</span> <span className="font-medium capitalize">{request.type}</span></p>
                    <p><span className="text-gray-600">Amount:</span> <span className="font-bold text-lg">{formatCurrency(request.amount)}</span></p>
                    <p><span className="text-gray-600">Requested:</span> {formatDate(request.requestedOn)}</p>
                    <p><span className="text-gray-600">Reason:</span></p>
                    <p className="text-gray-900 bg-gray-50 p-2 rounded">{request.reason}</p>
                    {request.emiMonths && (
                      <p><span className="text-gray-600">EMI:</span> {formatCurrency(request.emiAmount)}/month × {request.emiMonths}</p>
                    )}
                    {request.remainingAmount > 0 && (
                      <p><span className="text-gray-600">Remaining:</span> <span className="font-semibold text-orange-600">{formatCurrency(request.remainingAmount)}</span></p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Actions</h3>
                  <div className="space-y-2">
                    {request.status === 'pending' && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowApproveModal(true);
                          }}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowRejectModal(true);
                          }}
                          className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </>
                    )}

                    {request.status === 'approved' && (
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDisburseModal(true);
                        }}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Send className="w-4 h-4" />
                          Mark as Disbursed
                        </button>
                    )}

                    {request.status === 'disbursed' && (
                      <div className="text-center text-sm text-gray-600">
                        <TrendingUp className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                        Disbursed on {formatDate(request.disbursedOn)}
                      </div>
                    )}

                    {request.status === 'repaid' && (
                      <div className="text-center text-sm text-gray-600">
                        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        Fully Repaid
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Approve Request</h2>
            
            {selectedRequest.type === 'loan' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  EMI Months <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={emiMonths}
                  onChange={(e) => setEmiMonths(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Enter number of months"
                  min="1"
                />
                {emiMonths > 0 && (
                  <p className="text-sm text-gray-600 mt-2">
                    EMI: {formatCurrency(selectedRequest.amount / emiMonths)}/month
                  </p>
                )}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments (Optional)
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                rows="3"
                placeholder="Add any comments..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setSelectedRequest(null);
                  setEmiMonths('');
                  setComments('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Reject Request</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                rows="4"
                placeholder="Explain why this request is being rejected..."
                required
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedRequest(null);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disburse Modal */}
      {showDisburseModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Mark as Disbursed</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Disbursement Mode
              </label>
              <select
                value={disbursementMode}
                onChange={(e) => setDisbursementMode(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference Number (Optional)
              </label>
              <input
                type="text"
                value={disbursementReference}
                onChange={(e) => setDisbursementReference(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Transaction ID or reference"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDisburseModal(false);
                  setSelectedRequest(null);
                  setDisbursementMode('bank_transfer');
                  setDisbursementReference('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDisburse}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvanceManagement;
